import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { AppointmentTemplateEnvelope } from '@/types/settings-api';
import { appointmentTemplateListQueryKey } from './use-appointment-template-list';

/**
 * Save the practitioner's appointment-template list (PROD-762).
 *   POST /api/Doctor/AppointmentTemplate
 *
 * **Wire-shape traps captured from the dev cURL 2026-05-20**:
 *
 *   1. Body is the envelope `{ request: [...], isDeleted: [...] }` —
 *      distinct from `useSaveCalendarSettings` (bare array) and from
 *      `useSaveAppointmentProfile` (which uses `createAppointmentProfileRequest`
 *      as the wrapper key). Three different envelope shapes on the
 *      same Schedule tab; don't harmonise.
 *
 *   2. **`practitionerId` on the body is CORRECTLY spelled** — distinct
 *      from the typo'd `practionerId` on the GET-list query string
 *      (`use-appointment-template-list.ts:62`) AND distinct from the
 *      typo'd `practionerId` on the AppointmentProfile body. Yes, same
 *      Schedule tab uses both spellings depending on field placement
 *      and parent record. See `AppointmentTemplateEntry` doc for the
 *      full quirk story.
 *
 *   3. `weekDay` is a STRING ARRAY `["Thursday"]` — a single template
 *      can apply to multiple weekdays. Empty array = no days selected
 *      (UI should treat as invalid before allowing save).
 *
 *   4. `startTime` / `endTime` are structured 12-hour-clock objects:
 *      `{ hour: 9, minute: 0, format: "AM" }`. NOT a single time
 *      string. Hour is 1–12 (NOT 0–23); format is `"AM"` or `"PM"`
 *      verbatim. Mirrors Flutter `Template.startTime`.
 *
 *   5. **`isUpdated: true`** flags entries the user changed in this
 *      submission; `false` for unchanged. Server reads this to decide
 *      which rows to write through. Same list-snapshot semantics as
 *      `useSaveCalendarSettings`. The user-edited row carries true;
 *      every preserved row carries false; deleted rows go into the
 *      `isDeleted` array by GUID and are removed from `request`.
 *
 * Flutter parity: `EnableProviderScheduleEvent` dispatch — the bloc
 * assembles the full envelope from the current bloc state. Cache
 * invalidation: only the appointment-template-list slot.
 */
export function useSaveAppointmentTemplate(
  doctorSetupId: string,
  organizationId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (envelope: AppointmentTemplateEnvelope) => {
      return post<unknown>('/api/Doctor/AppointmentTemplate', envelope);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appointmentTemplateListQueryKey(doctorSetupId, organizationId),
      });
    },
  });
}
